import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AuthContext = createContext(null);

// Friendly translation for backend error strings the user should never see raw.
const FRIENDLY_DETAIL_MAP = {
  "Not authenticated": "Sessione scaduta. Effettua nuovamente il login.",
  "NOT AUTHENTICATED": "Sessione scaduta. Effettua nuovamente il login.",
  "Could not validate credentials": "Sessione scaduta. Effettua nuovamente il login.",
  "Invalid setup token": "Token di setup non valido.",
};

export function formatApiError(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") {
    return FRIENDLY_DETAIL_MAP[detail] || FRIENDLY_DETAIL_MAP[detail.trim()] || detail;
  }
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

let _interceptorInstalled = false;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Install a global 401 interceptor exactly once: when the cookie is missing
  // or expired, downgrade the noisy "Not authenticated" payload to a silent
  // "user=false" so the router can show the login screen instead of leaking
  // raw FastAPI errors into toasts.
  useEffect(() => {
    if (_interceptorInstalled) return;
    _interceptorInstalled = true;
    axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        if (status === 401 && !url.includes("/auth/login") && !url.includes("/auth/me")) {
          setUser(false);
          // Rewrite the detail so any consumer that still surfaces it gets a friendly string.
          const detail = error?.response?.data?.detail;
          if (typeof detail === "string" && FRIENDLY_DETAIL_MAP[detail]) {
            error.response.data.detail = FRIENDLY_DETAIL_MAP[detail];
          }
        }
        return Promise.reject(error);
      },
    );
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, setUser }),
    [user, loading, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

/** True only for users with `role === "admin"`. Client/user roles get false. */
export function useIsAdmin() {
  const { user } = useContext(AuthContext);
  return user?.role === "admin";
}
